<script setup lang="ts">
  import { storeToRefs } from 'pinia';
  import { ref } from 'vue';

  import BvBtn from '@bv/components/form/button/BvBtn.vue';
  import BvTimer from '@bv/components/timer/BvTimer.vue';
  import { movable as vMovable, type MovableOptions } from '@bv/directives';
  import { useAuthStore, useUserStore } from '@bv/stores';
  import { trans } from '@bv/utils';

  import ClockIcon from './partials/icons/ClockIcon.vue';
  import CloseIcon from './partials/icons/CloseIcon.vue';
  import CollapseIcon from './partials/icons/CollapseIcon.vue';
  import ExpandIcon from './partials/icons/ExpandIcon.vue';
  import UserIcon from './partials/icons/UserIcon.vue';
  import WorkspaceIcon from './partials/icons/WorkspaceIcon.vue';

  const userStore = useUserStore();
  const authStore = useAuthStore();

  const { userDetails, currentWorkspace } = storeToRefs(userStore);
  const { impersonateTokenExpiresAt: time } = storeToRefs(authStore);

  const isExpanded = ref<boolean>(true);

  const isShown = ref<boolean>(true);

  const isLoggingOut = ref<boolean>(false);

  const logout = (): void => {
    isLoggingOut.value = true;
    authStore.endImpersonationSession();
    isLoggingOut.value = false;
  };

  const movableOptions: MovableOptions = {
    area: '.bv-personation-widget-area',
    position: 'top-end',
    fixed: true,
  };
</script>

<template>
  <div class="bv-personation-widget-area fixed inset-4 -z-10" />
  <transition>
    <div
      v-if="userDetails && isShown"
      v-movable="movableOptions"
      class="fixed z-50 w-80 max-w-[calc(100%-var(--spacing)*8)] rounded-lg border border-gray-50 bg-white font-medium shadow shadow-black/10"
    >
      <div class="flex items-center gap-1 border-b border-gray-200 px-2 py-1.5">
        <div class="grow text-gray-900">
          {{ trans('global.impersonate.impersonate_user_trans') }}
        </div>
        <bv-btn
          class="h-8 w-8 p-1.5"
          color="var(--color-gray-800)"
          rounded="xs"
          variant="text"
          @click="isExpanded = !isExpanded"
        >
          <collapse-icon v-if="isExpanded" />
          <expand-icon v-else />
        </bv-btn>
        <bv-btn
          class="h-8 w-8 p-1.5"
          color="var(--color-gray-800)"
          rounded="xs"
          variant="text"
          @click="isShown = false"
        >
          <close-icon />
        </bv-btn>
      </div>
      <div class="mx-4 mt-3 mb-4">
        <div
          class="grid leading-4 text-gray-500 [&_>*]:flex [&_>*]:items-center [&_>*]:gap-2 [&_>*]:py-2"
          :class="{
            '[&_>*]:border-gray-200 [&_>*:not(:last-child)]:border-b': isExpanded,
            'grid-cols-2 gap-2': !isExpanded,
          }"
        >
          <div :class="{ 'order-1': !isExpanded }">
            <user-icon
              v-if="isExpanded"
              class="h-4 w-4 min-w-4"
            />
            <div>{{ userDetails.username }}</div>
          </div>
          <div v-if="isExpanded">
            <workspace-icon class="h-4 w-4 min-w-4" />
            <div>{{ currentWorkspace?.name }}</div>
          </div>
          <div class="grow">
            <clock-icon class="h-4 w-4 min-w-4" />
            <bv-timer
              v-if="time"
              class="text-gray-700"
              :start-time="time"
            />
          </div>
        </div>
        <bv-btn
          v-if="isExpanded"
          class="mt-2.5 w-full bg-orange-50"
          color="var(--color-orange-500)"
          :loading="isLoggingOut"
          :text="trans('global.impersonate.end_session_trans')"
          variant="outlined"
          @click="logout()"
        />
      </div>
    </div>
  </transition>
</template>

<style lang="scss" scoped>
  .v-enter-active,
  .v-leave-active {
    transition: opacity 0.7s ease;
  }

  .v-enter-from,
  .v-leave-to {
    opacity: 0;
  }
</style>
